import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServiceDeskUserDisplayMap } from "@sd/hooks/useServiceDeskUserDisplayMap";
import { normalizeTicketCreateDetailPayload } from "@sd/lib/ticketCreateDetail";
import { extractSlaPolicyGridRows } from "@sd/lib/ticketStreamUtils";
import { workbenchTicketFromDetailPayload } from "@sd/lib/workbenchTicketFromApi";
import ServiceDeskAPIService, {
  serviceDeskTicketCreateDetailQueryKey,
} from "@sd/services/APIs";

/** Fetch single-ticket detail for agent workbench — source of truth for the detail pane. */
export function useAgentWorkbenchTicketDetail(ticketId: string | null | undefined) {
  const { userDisplayMap, agentEmployees, priorityIdToLabel } =
    useServiceDeskUserDisplayMap({ refetchAgentListsOnMount: false });

  const { data: slaPolicyData } = useQuery({
    queryKey: ["sla-policy-map"],
    queryFn: () => ServiceDeskAPIService.formily.getGridData("SLA Policy", 1, 100),
    staleTime: 5 * 60 * 1000,
  });

  const slaPriorityById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const row of extractSlaPolicyGridRows(slaPolicyData)) {
      if (row.id && row.priority) map[String(row.id)] = String(row.priority);
    }
    return map;
  }, [slaPolicyData]);

  const {
    data: detailPayload,
    isPending,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: serviceDeskTicketCreateDetailQueryKey(ticketId),
    queryFn: async () => {
      const res = await ServiceDeskAPIService.tickets.getCreatedTicket(
        ticketId!,
        { skipErrorNotification: true }
      );
      if (res.data === undefined || res.data === null) {
        throw new Error("Ticket detail failed");
      }
      return normalizeTicketCreateDetailPayload(res.data);
    },
    enabled: Boolean(ticketId),
    staleTime: 0,
    refetchOnMount: "always",
    retry: 1,
  });

  const isNotFound =
    isError &&
    (error as { response?: { status?: number } } | undefined)?.response
      ?.status === 404;

  const ticket = useMemo(() => {
    if (!ticketId || !detailPayload) return null;
    return workbenchTicketFromDetailPayload(
      detailPayload,
      priorityIdToLabel,
      userDisplayMap,
      agentEmployees,
      slaPriorityById
    );
  }, [
    ticketId,
    detailPayload,
    priorityIdToLabel,
    userDisplayMap,
    agentEmployees,
    slaPriorityById,
  ]);

  return {
    ticket,
    isLoading: Boolean(ticketId) && isPending,
    isFetching,
    isError,
    isNotFound,
  };
}
